import { observer } from 'mobx-react-lite';
import React, { type ReactNode } from 'react';
import { bookingSummaryState } from '#/components/BookingSummaryPanel/BookingSummaryContext';
import { BookingSummaryEditLink } from '#/components/BookingSummaryPanel/components/BookingSummaryEditLink';
import BookingSummaryPaymentRows from '#/components/BookingSummaryPanel/components/BookingSummaryPaymentRows';
import { StickyPanel } from '#/components/BookingSummaryPanel/components/StickyPanel';
import { TripInfoSummary } from '#/components/BookingSummaryPanel/components/TripInfoSummary';
import { useCanEditSelectedReservation } from '#/hooks/useCanEditSelectedReservation';
import { useIsSmallView } from '#/hooks/useIsSmallView';
import { useMapLegacyFleetCategory } from '#/hooks/useMapLegacyFleetCategory';
import { type CurrencyValue, formatCurrency } from '#/lib/currency';
import { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import { mergeSx } from '@jucy-ui/appearance';
import { ValidateUpdateResponse } from '@jucy-ui/common';
import { Money } from '@jucy/rentals-api-client';
import { BoxProps, type SxProps, type Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import Currency from '../Currency';
import AgentBookingCard from './components/AgentBookingCard';
import { AmendmentActions } from './components/AmendmentActions';
import { BookingSummaryBalanceDue } from './components/BookingSummaryBalanceDue';
import BookingSummaryPanelProductRow from './components/BookingSummaryPanelProductRow';
import BookingSummaryPanelRow from './components/BookingSummaryPanelRow';
import BookingSummaryPanelSection from './components/BookingSummaryPanelSection';
import { BookingSummaryPanelTitle } from './components/BookingSummaryPanelTitle';
import { FleetCategorySummaryRow } from './components/FleetCategorySummaryRow';

export interface BookingSummaryPanelContentProps extends BoxProps {
    reservation?: RentalsReservationInstance;
    amendment?: ValidateUpdateResponse;
    showEdit?: boolean;
}

const BookingSummaryPanelContent: React.FC<BookingSummaryPanelContentProps> = observer(function BookingSummaryPanelContentComponent({
    reservation,
    amendment,
    className,
    sx,
    showEdit = false,
    ...props
}) {
    const { data: fleetCategory } = useMapLegacyFleetCategory(reservation?.fleetCategory);
    const isSm = useIsSmallView();
    const isEditable = useCanEditSelectedReservation(showEdit);
    if (!reservation || !fleetCategory) {
        return null;
    }

    const merchantFees = reservation.payments?.filter((p) => p.merchantFeeAmount?.value > 0);
    const lines = (amendment?.updates?.lines || reservation.lines)
        .filter((l) => l.category !== 'InsuranceFee')
        .sort((a, b) => a.sortOrder - b.sortOrder);
    const amendedExcess =
        amendment?.updates?.lines?.find((l) => l.category === 'InsuranceFee' && !l.isExcluded) ??
        amendment?.updates?.lines?.find((l) => l.category === 'InsuranceFee');
    const activeExcess = reservation.insuranceLine || amendedExcess;
    const amendedTotal =
        amendment?.updates?.totalPrice && amendment.updates.totalPrice.value !== reservation.computedTotal.value
            ? amendment.updates.totalPrice
            : undefined;
    return (
        <Box className={className} {...props} sx={{ paddingX: { xs: 0, lg: 1 } }}>
            <StickyPanel sx={{ paddingBottom: isSm ? 18 : 2 }}>
                <Box
                    sx={{
                        bgcolor: isSm ? undefined : 'background.paper',
                        position: isSm ? undefined : 'sticky',
                        top: isSm ? 0 : 80,
                        paddingY: isSm ? undefined : 2,
                    }}
                >
                    {bookingSummaryState.isPartnerBooking ? <AgentBookingCard reservation={reservation} /> : null}
                    {!bookingSummaryState.collapsable ? (
                        <>
                            <BookingSummaryPanelTitle reservation={reservation} showEdit={showEdit} />
                            <Divider />
                        </>
                    ) : null}
                </Box>
                <TripInfoSummary
                    title={isEditable ? 'DATES & LOCATION' : 'Trip Info'}
                    sx={{ mt: 2 }}
                    gutterBottom={true}
                    reservation={reservation}
                    showEdit={showEdit}
                />

                <BookingSummaryPanelSection title="Vehicle" gutterBottom={true}>
                    <FleetCategorySummaryRow fleetCategory={fleetCategory} amendedFleetCategory={amendment?.updates?.fleetCategory} />
                </BookingSummaryPanelSection>

                {/*Insurance*/}
                <BookingSummaryPanelSection
                    edit={
                        isEditable ? (
                            <BookingSummaryEditLink
                                href={`/self-service/reservation/${reservation.reservationReference}/edit?action=excess-reduction`}
                            />
                        ) : null
                    }
                    title="Excess Reduction"
                    gutterBottom={true}
                >
                    {activeExcess ? (
                        <BookingSummaryPanelProductRow
                            item={activeExcess}
                            amendedItem={reservation.insuranceLine && amendedExcess ? amendedExcess : undefined}
                        />
                    ) : (
                        <BookingSummaryPanelRow title="Excess reduction hasn’t been added to this booking" />
                    )}
                </BookingSummaryPanelSection>

                <BookingSummaryPanelSection
                    edit={
                        isEditable ? (
                            <BookingSummaryEditLink href={`/self-service/reservation/${reservation.reservationReference}/edit?action=extras`} />
                        ) : null
                    }
                    title="Extras & Fees"
                    gutterBottom={true}
                >
                    {lines.length || merchantFees.length ? (
                        <>
                            {lines?.map((line, i) => (
                                <BookingSummaryPanelProductRow key={`${line.productId}-${i}`} item={line} />
                            ))}
                            {merchantFees?.map((payment, i) => (
                                <BookingSummaryPanelRow
                                    title={payment.merchantFeeName}
                                    subTitle={
                                        <>
                                            {format(payment.date, 'dd MMM yyyy')} <em>({payment.type})</em>
                                        </>
                                    }
                                    key={`merchant-fee-${payment.merchantFeeName}-${payment.merchantFeeAmount}-${i}`}
                                    value={formatCurrency(payment.merchantFeeAmount as Required<Money>)}
                                />
                            ))}
                        </>
                    ) : (
                        <BookingSummaryPanelRow title="No extras or optional add-ons selected" />
                    )}
                </BookingSummaryPanelSection>

                {!bookingSummaryState.isPartnerBooking ? (
                    <>
                        <TotalRow
                            label="Total"
                            amount={reservation.computedTotal}
                            sx={amendedTotal ? { textDecoration: 'line-through' } : undefined}
                        />
                        {amendedTotal ? <TotalRow label="New total" amount={amendedTotal} /> : null}
                    </>
                ) : null}

                {/*Payments*/}
                {reservation.payments && reservation.payments.length && !bookingSummaryState.isPartnerBooking ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <BookingSummaryPanelSection title={'Payments'}>
                            <BookingSummaryPaymentRows payments={reservation.payments} />
                        </BookingSummaryPanelSection>
                    </>
                ) : null}

                {isEditable && amendment ? (
                    <AmendmentActions reservation={reservation} amendment={amendment} />
                ) : (
                    <BookingSummaryBalanceDue reservation={reservation} amendment={amendment} />
                )}
            </StickyPanel>
        </Box>
    );
});

type TotalRowProps = {
    label: ReactNode;
    amount?: CurrencyValue;
    sx?: SxProps<Theme>;
};
const TotalRow: React.FC<TotalRowProps> = ({ label, amount, sx }) => (
    <Stack direction="row" sx={mergeSx({ justifyContent: 'space-between' }, sx)}>
        <Typography variant="button" sx={{ fontWeight: 700 }}>
            {label}
        </Typography>
        <Typography variant="button" align="right">
            <Currency>{amount}</Currency>
        </Typography>
    </Stack>
);

export default BookingSummaryPanelContent;
